import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Mail, Phone, Save, User } from 'lucide-react';
import { useAuth } from '../components/AuthProvider';
import { fetchMember, updateMember } from '../lib/members-api';
import { needsProfileCompletion } from '../lib/profile-completion';
import { parseIndianMobile } from '../lib/phone';
import type { Member } from '../types';

export function CompleteProfilePage() {
  const { member: authMember, refresh, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Member | null>(authMember);
  const [name, setName] = useState(authMember?.name ?? '');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authMember) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    void fetchMember(authMember.uid)
      .then((member) => {
        if (cancelled) return;
        setProfile(member);
        setName(member.name);
        setPhone(member.phone ?? '');
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Could not load profile');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authMember]);

  useEffect(() => {
    if (authMember && !needsProfileCompletion(authMember)) {
      navigate('/', { replace: true });
    }
  }, [authMember, navigate]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;

    const trimmedName = name.trim();

    if (!trimmedName) {
      setError('Name cannot be empty');
      return;
    }
    const parsedPhone = parseIndianMobile(phone);
    if (!parsedPhone.ok) {
      setError(parsedPhone.error ?? 'Enter a valid mobile number');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await updateMember(profile.uid, {
        name: trimmedName,
        phone: parsedPhone.e164,
      });
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (!authMember) return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-surface-0)] p-6">
      <div className="w-full max-w-md bg-[var(--color-surface-1)] rounded-[var(--radius-xl)] p-8 md:p-10 shadow-[var(--shadow-lg)] border border-[var(--color-border)]">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-[var(--radius-md)] bg-[var(--color-surface-2)] flex items-center justify-center overflow-hidden">
            {profile?.photoURL ? (
              <img
                src={profile.photoURL}
                alt={profile.name}
                referrerPolicy="no-referrer"
                className="w-full h-full object-cover"
              />
            ) : (
              <User className="w-8 h-8 text-[var(--color-text-secondary)]" />
            )}
          </div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)] mb-2">
            Complete your profile
          </h1>
          <p className="text-[var(--color-text-secondary)] text-sm leading-relaxed">
            Your name and phone number will be used to contact you about events and activities.
            Please enter your correct details. You can also fix your display name if it looks wrong.
          </p>
        </div>

        {loading ? (
          <div className="h-48 bg-[var(--color-surface-2)] rounded-[var(--radius-lg)] animate-pulse" />
        ) : profile ? (
          <form onSubmit={(e) => void handleSave(e)} className="space-y-4">
            <div>
              <label
                htmlFor="complete-profile-email"
                className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5"
              >
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-tertiary)]" />
                <p
                  id="complete-profile-email"
                  className="py-3 pl-10 pr-4 text-sm text-[var(--color-text-primary)] bg-[var(--color-surface-2)] rounded-[var(--radius-md)] border border-[var(--color-border)]"
                >
                  {profile.email}
                </p>
              </div>
            </div>

            <div>
              <label
                htmlFor="complete-profile-name"
                className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5"
              >
                Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-tertiary)]" />
                <input
                  id="complete-profile-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoComplete="name"
                  className="w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-3 pl-10 pr-4 text-sm outline-none focus:border-[var(--color-accent)]"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="complete-profile-phone"
                className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5"
              >
                Phone <span className="text-[var(--color-error)]">*</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-tertiary)]" />
                <input
                  id="complete-profile-phone"
                  type="tel"
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  autoComplete="tel"
                  placeholder="10-digit mobile number"
                  className="w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-3 pl-10 pr-4 text-sm outline-none focus:border-[var(--color-accent)]"
                />
              </div>
              <p className="text-xs text-[var(--color-text-tertiary)] mt-1.5">
                Indian mobile number. We may call or message you on WhatsApp.
              </p>
            </div>

            {error && <p className="text-sm text-[var(--color-error)]">{error}</p>}

            <button
              type="submit"
              disabled={saving}
              className="w-full inline-flex items-center justify-center gap-2 bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white font-medium py-3 px-6 rounded-[var(--radius-md)] text-sm disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Continue to app'}
            </button>
          </form>
        ) : (
          <p className="text-sm text-[var(--color-error)]">{error ?? 'Could not load profile.'}</p>
        )}

        <button
          type="button"
          onClick={() => void signOut()}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
      </div>
    </div>
  );
}
